import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    InputNumber,
    Rate,
    message,
    Popconfirm,
    Space,
    Card,
    Typography,
    Row,
    Col,
    Tag,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    StarOutlined,
    UserOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function Testimonials() {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetTestimonials();
            if (response.success) {
                setTestimonials(response.data.data || []);
            }
        } catch (error) {
            message.error('Failed to fetch testimonials');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingTestimonial) {
                await api.adminUpdateTestimonial(editingTestimonial.id, values);
                message.success('Testimonial updated successfully');
            } else {
                await api.adminCreateTestimonial(values);
                message.success('Testimonial created successfully');
            }
            setModalVisible(false);
            setEditingTestimonial(null);
            form.resetFields();
            fetchTestimonials();
        } catch (error) {
            message.error('Failed to save testimonial');
        }
    };

    const handleEdit = (testimonial) => {
        setEditingTestimonial(testimonial);
        form.setFieldsValue({
            ...testimonial,
            is_featured: testimonial.is_featured,
            is_active: testimonial.is_active,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteTestimonial(id);
            message.success('Testimonial deleted successfully');
            fetchTestimonials();
        } catch (error) {
            message.error('Failed to delete testimonial');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    {record.company && (
                        <div className="text-sm text-gray-500">{record.company}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            render: (location) => (
                <div className="flex items-center">
                    <EnvironmentOutlined className="mr-1" />
                    {location}
                </div>
            ),
        },
        {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            render: (rating) => <Rate disabled value={rating} />,
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
            ellipsis: true,
            render: (content) => (
                <div className="max-w-xs">
                    <div className="truncate">{content}</div>
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space>
                    {record.is_featured && <Tag color="gold">Featured</Tag>}
                    {record.is_active ? (
                        <Tag color={getStatusColor('success')}>Active</Tag>
                    ) : (
                        <Tag color={getStatusColor('cancelled')}>Inactive</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this testimonial?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>Testimonials Management</Title>
                <p className="text-gray-600">
                    Manage customer testimonials and reviews for your homepage
                </p>
            </div>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <div>
                        <Title level={4} className="!mb-0">
                            All Testimonials ({testimonials.length})
                        </Title>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingTestimonial(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                    >
                        Add Testimonial
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={testimonials}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} testimonials`,
                    }}
                />
            </Card>

            <Modal
                title={editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingTestimonial(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        rating: 5,
                        is_featured: false,
                        is_active: true,
                        sort_order: 0,
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Customer Name"
                                rules={[{ required: true, message: 'Please enter customer name' }]}
                            >
                                <Input placeholder="Enter customer name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="location"
                                label="Location"
                            >
                                <Input placeholder="Enter customer location" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="company"
                                label="Company"
                            >
                                <Input placeholder="Enter company name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="position"
                                label="Position"
                            >
                                <Input placeholder="Enter position/role" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="rating"
                        label="Rating"
                        rules={[{ required: true, message: 'Please select rating' }]}
                    >
                        <Rate />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Testimonial Content"
                        rules={[{ required: true, message: 'Please enter testimonial content' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter the testimonial content..."
                            maxLength={2000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        name="avatar"
                        label="Avatar URL"
                    >
                        <Input placeholder="Enter avatar image URL" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="is_featured"
                                label="Featured"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="is_active"
                                label="Active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="sort_order"
                                label="Sort Order"
                            >
                                <InputNumber min={0} placeholder="0" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="flex justify-end space-x-2">
                        <Button onClick={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingTestimonial ? 'Update' : 'Create'} Testimonial
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default Testimonials;
